const STORAGE_PREFIX = 'thetavern:night-draft:v1';
const MAX_DRAFT_AGE_MS = 14 * 24 * 60 * 60 * 1000;

const isBrowserStorageAvailable = () =>
  typeof window !== 'undefined' && Boolean(window.localStorage);

const storageKey = (nightId) =>
  `${STORAGE_PREFIX}:${nightId ? `edit:${nightId}` : 'new'}`;

const normalizeIdList = (items) =>
  Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item))
    )
  );

const sanitizeFilters = (filters = {}) => ({
  minPlayers: filters.minPlayers || '',
  maxPlayers: filters.maxPlayers || '',
  maxTime: filters.maxTime || '',
});

export const buildNightDraft = ({
  step,
  title,
  date,
  time,
  place,
  placePreset,
  invited,
  chosenGames,
  gameSearchQuery,
  gameFilters,
  gameSort,
  requiresResponse,
}) => ({
  version: 1,
  updatedAt: Date.now(),
  step: Number.isFinite(Number(step)) ? Number(step) : 0,
  title: title || '',
  date: date || '',
  time: time || '',
  place: place || '',
  placePreset: placePreset || 'myPlace',
  invited: normalizeIdList(invited),
  chosenGames: normalizeIdList(chosenGames),
  gameSearchQuery: gameSearchQuery || '',
  gameFilters: sanitizeFilters(gameFilters),
  gameSort: gameSort || '',
  requiresResponse: requiresResponse !== false,
});

export const saveNightDraft = (nightId, draft) => {
  if (!isBrowserStorageAvailable()) return;

  try {
    window.localStorage.setItem(storageKey(nightId), JSON.stringify(buildNightDraft(draft)));
  } catch {
    // Draft persistence is a convenience layer; quota/private-mode failures must not block the app.
  }
};

export const readNightDraft = (nightId) => {
  if (!isBrowserStorageAvailable()) return null;

  try {
    const raw = window.localStorage.getItem(storageKey(nightId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const updatedAt = Number(parsed?.updatedAt);

    if (!updatedAt || Date.now() - updatedAt > MAX_DRAFT_AGE_MS) {
      window.localStorage.removeItem(storageKey(nightId));
      return null;
    }

    return buildNightDraft(parsed);
  } catch {
    window.localStorage.removeItem(storageKey(nightId));
    return null;
  }
};

export const clearNightDraft = (nightId) => {
  if (!isBrowserStorageAvailable()) return;

  try {
    window.localStorage.removeItem(storageKey(nightId));
  } catch {
    // Ignore storage failures for the same reason as saveNightDraft.
  }
};

export const isBrowserReloadNavigation = () => {
  if (typeof window === 'undefined' || !window.performance) return false;

  const navigationEntry = window.performance.getEntriesByType?.('navigation')?.[0];
  if (navigationEntry?.type) return navigationEntry.type === 'reload';

  return window.performance.navigation?.type === 1;
};

export const shouldReadNightDraft = ({
  isEditing,
  isReloadNavigation,
  isInitialNewNightRoute = false,
}) =>
  Boolean(isEditing || (isReloadNavigation && isInitialNewNightRoute));

export const shouldResetNewNightDraftState = ({
  isEditing,
  isPersistedPageShow,
  hasCompletedNewNightFlow,
}) =>
  Boolean(!isEditing && isPersistedPageShow && hasCompletedNewNightFlow);

export const __nightDraftStorageKey = storageKey;
